import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassTextarea - Executive design system textarea component
 *
 * A premium textarea with glass morphism effects, character counter,
 * and smooth transitions. Matches the visual style of other Glass components.
 *
 * @example
 * ```tsx
 * <GlassTextarea
 *   value={description}
 *   onChange={(value) => setDescription(value)}
 *   label="Description"
 *   placeholder="Enter a detailed description..."
 *   maxLength={1000}
 *   showCharCount
 * />
 * ```
 */

export interface GlassTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  /** Current textarea value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Optional label */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text below the textarea */
  helperText?: string;
  /** Number of visible rows */
  rows?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Show character count indicator */
  showCharCount?: boolean;
  /** Expand to full width */
  fullWidth?: boolean;
  /** Allow vertical resize */
  resizable?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const GlassTextarea = forwardRef<HTMLTextAreaElement, GlassTextareaProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      helperText,
      rows = 4,
      maxLength,
      showCharCount = false,
      fullWidth = true,
      resizable = true,
      className,
      disabled,
      id,
      required,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `glass-textarea-${Math.random().toString(36).substr(2, 9)}`;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    };

    const charCount = value?.length || 0;
    const isOverLimit = maxLength && charCount > maxLength;

    const textareaClasses = cn(
      // Base styles
      'block w-full min-h-[80px]',
      'rounded-xl',
      'border border-border-primary',
      'bg-white/80 backdrop-blur-sm dark:bg-slate-800/80',
      'text-foreground placeholder-text-tertiary',
      'px-4 py-3 text-base',
      'transition-all duration-200',
      // Focus styles
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 focus:border-[var(--accent)]',
      // Hover styles
      'hover:border-border-primary hover:bg-white/90 dark:hover:bg-slate-700/90',
      // Error state
      (error || isOverLimit) && 'border-rose-500/50 focus:ring-rose-500/40 focus:border-rose-500',
      // Disabled state
      disabled && 'opacity-50 cursor-not-allowed bg-bg-tertiary dark:bg-bg-secondary',
      // Resize
      resizable ? 'resize-y' : 'resize-none',
      // Custom classes
      className
    );

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {(label || (showCharCount && maxLength)) && (
          <div className="flex items-baseline justify-between mb-1.5">
            {label && (
              <label
                htmlFor={textareaId}
                className="text-sm font-medium text-muted-foreground"
              >
                {label}
                {required && <span className="text-rose-500 ml-1">*</span>}
              </label>
            )}
            {showCharCount && maxLength && (
              <span
                className={cn(
                  'text-xs tabular-nums',
                  isOverLimit
                    ? 'text-rose-500 font-medium'
                    : 'text-muted-foreground/60'
                )}
              >
                {charCount}/{maxLength}
              </span>
            )}
          </div>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          rows={rows}
          maxLength={maxLength}
          className={textareaClasses}
          {...props}
        />

        {error && (
          <p className="mt-1.5 text-sm text-rose-500">{error}</p>
        )}

        {helperText && !error && (
          <p className="mt-1.5 text-sm text-muted-foreground/60">{helperText}</p>
        )}
      </div>
    );
  }
);

GlassTextarea.displayName = 'GlassTextarea';
